import { useState } from 'react'

import { useAppSelector } from 'src/Hooks/redux'
import { getCampaigns } from 'src/Redux/campaignList-process'
import {
  getUserId,
  getUserPhone,
} from 'src/Redux/auth-process/userSlice/selectors'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
  CampaignTypeEnum,
} from 'src/Types/campaigns'
import { ID } from 'src/Types/common'

export const SMS_APPROVAL_QUALIFYING_PLATFORMS: string[] = [
  CampaignPlatformTypeEnum.SPOTIFY,
  CampaignPlatformTypeEnum.TIKTOK,
]

export const SMS_APPROVAL_QUALIFYING_STATUSES: string[] = [
  CampaignStatusEnum.PENDING_APPROVAL,
  CampaignStatusEnum.AWAITING_REVIEW,
]

type ManualModalState = 'open' | 'closed' | null

const getDismissedStorageKey = (userId: ID): string =>
  `sms_approval_modal_dismissed_${userId}`

const isDismissed = (userId: ID | null | undefined): boolean => {
  if (!userId) {
    return false
  }
  return localStorage.getItem(getDismissedStorageKey(userId)) === 'true'
}

interface UseSmsApprovalModalHook {
  isOpen: boolean
  isEligible: boolean
  openModal: () => void
  closeModal: () => void
}

export const useSmsApprovalModal = (): UseSmsApprovalModalHook => {
  const savedPhone = useAppSelector(getUserPhone)
  const campaigns = useAppSelector(getCampaigns)
  const userId = useAppSelector(getUserId)

  const [manualState, setManualState] = useState<ManualModalState>(null)

  const isEligible = Boolean(
    !savedPhone &&
    campaigns.some(
      (campaign) =>
        SMS_APPROVAL_QUALIFYING_PLATFORMS.includes(campaign.platformType) &&
        SMS_APPROVAL_QUALIFYING_STATUSES.includes(campaign.status),
    ),
  )

  const hasFirstQualifyingCampaign = campaigns.some(
    (campaign) =>
      SMS_APPROVAL_QUALIFYING_PLATFORMS.includes(campaign.platformType) &&
      SMS_APPROVAL_QUALIFYING_STATUSES.includes(campaign.status) &&
      'type' in campaign &&
      campaign.type === CampaignTypeEnum.FIRST,
  )

  const shouldAutoOpen =
    isEligible && hasFirstQualifyingCampaign && !isDismissed(userId)

  const isOpen =
    manualState === 'open' || (manualState !== 'closed' && shouldAutoOpen)

  const openModal = (): void => {
    setManualState('open')
  }

  const closeModal = (): void => {
    if (userId) {
      localStorage.setItem(getDismissedStorageKey(userId), 'true')
    }
    setManualState('closed')
  }

  return { isOpen, isEligible, openModal, closeModal }
}
